import { CUSTOM_REQUEST_HEADERS } from '../../constants'
import { RequestErrorType } from '../utils/parseError'
import { parseSubdomain } from '../../helpers/stringUtils'
import { REFRESH_TOKEN_EXPIRED } from '../../strings'

/** Distinct from REFRESH_TOKEN_EXPIRED on purpose: this one must not end a session. */
const REFRESH_REQUEST_FAILED = 'The refresh request failed.'

/**
 * Whether the backend looked at the token and said no, as opposed to failing on
 * the way there. `@nestjs/apollo` maps only 400/401/403/422 onto GraphQL codes,
 * so an internal fault arrives as INTERNAL_SERVER_ERROR and is correctly not a
 * refusal here.
 */
function isRefusal(errors: ReadonlyArray<{ extensions?: { code?: unknown } }>): boolean {
  return errors.some((error) => error?.extensions?.code === RequestErrorType.UNAUTHENTICATED)
}

// Can not use `useMutation` here coz `useMutation` needs to be inside `ApolloProvider`.
export const refreshAccessToken = async (refreshToken, graphQLEndpoint) => {
  const subdomain = parseSubdomain(window.location.hostname)
  const query = JSON.stringify({
    query: `mutation refreshAccessToken {
      refreshAccessToken(refreshToken: "${refreshToken}") {
        token
        refreshToken
      }
    }`,
  })
  const response = await fetch(graphQLEndpoint, {
    headers: {
      'content-type': 'application/json',
      [CUSTOM_REQUEST_HEADERS.SUBDOMAIN_HEADER_KEY]: subdomain,
      // Match the Apollo client so the fetch wrapper skips double-reporting 4xx (NOD-2829).
      'Apollo-Require-Preflight': 'true',
    },
    method: 'POST',
    body: query,
  })

  const responseJson = await response.json()
  const { data, errors } = responseJson
  if (errors) {
    // Only a REFUSAL ends the session. The caller clears the cookie jar when it
    // sees REFRESH_TOKEN_EXPIRED, and that jar is shared with Orchid — so
    // treating every errors array as a refusal signs the user out of BOTH
    // applications whenever the backend merely faults. Anything that is not an
    // explicit UNAUTHENTICATED is transient, and the session outlives it.
    if (isRefusal(errors)) {
      throw new Error(REFRESH_TOKEN_EXPIRED)
    }
    throw new Error(REFRESH_REQUEST_FAILED)
  }
  return data.refreshAccessToken
}
